import { forwardRef, useId } from 'react';

import { maskThousands, parseThousands } from '@alamo/utils';

import { cn } from '../../lib/cn';
import { LabelHint } from '../LabelHint/LabelHint';

/**
 * NumberInputWithUnit — number input with a static unit affix (e.g. `%`,
 * `R$`, `km`). Supports negative values out of the box and forwards the
 * current value as a string to keep React Hook Form happy.
 *
 * Used by ESCR-184.4 (Pilar 3 rule modal value field) and reusable for any
 * "valor com unidade" pattern in admin forms. Wraps a plain `<input
 * type="number">` because we want native browser numeric handling
 * (keyboard, increment/decrement, mobile keyboard switch) without rebuilding
 * it.
 */
export interface NumberInputWithUnitProps {
  label?: string;
  /**
   * Explicação exibida em tooltip via ícone Info ao lado do label.
   * Consumer passa valor de t(). Ignorado quando `label` está ausente.
   */
  labelHint?: string;
  /** Affix symbol — `%`, `R$`, `km`, `s`, etc. */
  unit: string;
  /** Where to render the unit. Default `suffix`. */
  position?: 'prefix' | 'suffix';
  /** Controlled value as a string ("" when empty). RHF-friendly. */
  value: string;
  /** Fired with the new string value. Uses `string` so empty state stays "". */
  onChange: (value: string) => void;
  /**
   * Display format for the value.
   * - `'number'` (default): native `<input type="number">` — keyboard
   *   stepper, decimals, no thousands separator.
   * - `'thousands'`: integer field with pt-BR thousands separator on display
   *   (`80000` → `80.000`). Switches the inner input to `type="text"` +
   *   `inputMode="numeric"`; `value`/`onChange` stay clean digit strings.
   *   `allowNegative`, `step` and decimal entry don't apply in this mode.
   */
  format?: 'number' | 'thousands';
  /** Defaults to `true` — admin pricing fields all accept negatives. */
  allowNegative?: boolean;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  name?: string;
  id?: string;
  testId?: string;
  size?: 'sm' | 'md' | 'lg';
  /**
   * Plan 15-18 (Pedro 2026-05-22): variant 'pill-big' per protótipo V4.
   * bg-ink/5 + rounded-2xl + font Ruda big pra valor + unit inline cinza.
   * Default 'default' = retro-compat com consumers existentes.
   */
  variant?: 'default' | 'pill-big';
}

const sizeClasses: Record<NonNullable<NumberInputWithUnitProps['size']>, string> = {
  sm: 'h-8 text-sm',
  md: 'h-10 text-sm',
  lg: 'h-12 text-base',
};

export const NumberInputWithUnit = forwardRef<HTMLInputElement, NumberInputWithUnitProps>(
  function NumberInputWithUnit(
    {
      label,
      labelHint,
      unit,
      position = 'suffix',
      value,
      onChange,
      allowNegative = true,
      min,
      max,
      step,
      placeholder,
      required = false,
      disabled = false,
      error,
      helperText,
      className,
      name,
      id: externalId,
      testId,
      size = 'md',
      variant = 'default',
      format = 'number',
    },
    ref,
  ) {
    const generatedId = useId();
    const inputId = externalId ?? `numinput-${generatedId}`;
    const helperId = `${inputId}-helper`;
    const errorId = `${inputId}-error`;

    const isThousands = format === 'thousands';
    // In thousands mode the input shows the grouped value but the controlled
    // `value` (and the value emitted by onChange) stays clean digits.
    const displayValue = isThousands ? maskThousands(value) : value;

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      const next = e.target.value;
      if (isThousands) {
        onChange(parseThousands(next));
        return;
      }
      // Block leading "-" when negatives are not allowed. Native input still
      // permits typing it, so we sanitize here. We don't strip in the middle
      // of a number to avoid eating the user's keystrokes mid-edit.
      if (!allowNegative && next.startsWith('-')) {
        onChange(next.replace(/^-/, ''));
        return;
      }
      onChange(next);
    }

    // Plan 15-18 (Pedro 2026-05-22): pill-big variant — bg-ink/5 + rounded-2xl
    // + font Ruda big pra valor + unit inline cinza. Match protótipo V4 Image #17.
    if (variant === 'pill-big') {
      return (
        <div className={cn('flex flex-col gap-2', className)}>
          {label && (
            <label htmlFor={inputId} className="text-sm font-poppins font-medium text-ink/80">
              {label}
              {(required || labelHint) && (
                <span className="whitespace-nowrap">
                  {required && <span className="ml-0.5 text-erro-apoio">*</span>}
                  {labelHint && <LabelHint hint={labelHint} />}
                </span>
              )}
            </label>
          )}
          <div
            className={cn(
              'flex items-center overflow-hidden rounded-2xl bg-ink/5',
              // Dark: o preenchimento `ink/α` some contra o card. O contorno neutro
              // delimita o campo e o preenchimento continua escuro, porque o valor
              // digitado clareia com o tema.
              'dark:bg-slate-200 dark:border dark:border-slate-400',
              'min-h-[68px] px-5 transition-all',
              'focus-within:bg-white focus-within:ring-2 focus-within:ring-laranja-encanto/30',
              'dark:focus-within:bg-slate-300',
              error && 'ring-2 ring-erro-apoio/40',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <input
              ref={ref}
              id={inputId}
              name={name}
              type={isThousands ? 'text' : 'number'}
              inputMode={isThousands ? 'numeric' : 'decimal'}
              value={displayValue}
              onChange={handleChange}
              min={isThousands ? undefined : min}
              max={isThousands ? undefined : max}
              step={isThousands ? undefined : step}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              aria-invalid={!!error || undefined}
              aria-describedby={error ? errorId : helperText ? helperId : undefined}
              data-testid={testId}
              className={cn(
                'min-w-0 flex-1 bg-transparent font-ruda text-3xl text-ink outline-none',
                'placeholder:text-ink/30 placeholder:font-ruda',
                'disabled:cursor-not-allowed disabled:text-ink/40',
                '[appearance:textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none',
              )}
            />
            <span
              aria-hidden
              className="ml-3 shrink-0 select-none font-poppins text-base font-medium text-ink/60"
            >
              {unit}
            </span>
          </div>
          {error ? (
            <p id={errorId} role="alert" className="text-xs font-poppins text-erro-apoio">
              {error}
            </p>
          ) : helperText ? (
            <p id={helperId} className="text-xs font-poppins text-ink/50">
              {helperText}
            </p>
          ) : null}
        </div>
      );
    }

    return (
      <div className={cn('flex flex-col gap-1', className)}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-body font-medium text-slate-700">
            {label}
            {(required || labelHint) && (
              <span className="whitespace-nowrap">
                {required && <span className="ml-0.5 text-alamo-red-500">*</span>}
                {labelHint && <LabelHint hint={labelHint} />}
              </span>
            )}
          </label>
        )}

        <div
          className={cn(
            'flex items-stretch overflow-hidden rounded-md border bg-white',
            'focus-within:ring-2 focus-within:ring-alamo-orange-500 focus-within:ring-offset-0',
            error ? 'border-alamo-red-500' : 'border-slate-300',
            disabled && 'cursor-not-allowed bg-slate-50',
          )}
        >
          {position === 'prefix' && (
            <UnitAffix unit={unit} side="prefix" disabled={disabled} size={size} />
          )}
          <input
            ref={ref}
            id={inputId}
            name={name}
            type={isThousands ? 'text' : 'number'}
            inputMode={isThousands ? 'numeric' : 'decimal'}
            value={displayValue}
            onChange={handleChange}
            min={isThousands ? undefined : min}
            max={isThousands ? undefined : max}
            step={isThousands ? undefined : step}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            aria-invalid={!!error || undefined}
            aria-describedby={error ? errorId : helperText ? helperId : undefined}
            data-testid={testId}
            className={cn(
              'min-w-0 flex-1 bg-transparent px-3 font-body text-secondary-950 outline-none',
              'placeholder:text-slate-400',
              'disabled:cursor-not-allowed disabled:text-slate-400',
              // Hide the up/down spinners — native widget keyboard support
              // stays through `inputMode=decimal`.
              '[appearance:textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none',
              sizeClasses[size],
            )}
          />
          {position === 'suffix' && (
            <UnitAffix unit={unit} side="suffix" disabled={disabled} size={size} />
          )}
        </div>

        {error ? (
          <p id={errorId} role="alert" className="text-xs text-alamo-red-500">
            {error}
          </p>
        ) : helperText ? (
          <p id={helperId} className="text-xs text-slate-500">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  },
);

interface UnitAffixProps {
  unit: string;
  side: 'prefix' | 'suffix';
  disabled?: boolean;
  size: 'sm' | 'md' | 'lg';
}

function UnitAffix({ unit, side, disabled, size }: UnitAffixProps) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex shrink-0 select-none items-center justify-center bg-slate-50 px-3 text-sm font-body font-medium text-slate-500',
        side === 'prefix' ? 'border-r border-slate-200' : 'border-l border-slate-200',
        disabled && 'text-slate-400',
        size === 'sm' && 'text-xs',
        size === 'lg' && 'text-base',
      )}
    >
      {unit}
    </span>
  );
}
